/**
 * One definition of "in range" for numeric inputs.
 *
 * The module had three: `NumberInput` clamped on blur, the WPS day fields each
 * clamped in their own way, and the Formik money fields did not clamp at all —
 * so the same mistake (a value past `max`) was caught, silently corrected, or
 * saved depending on which widget the field happened to use.
 */
export type NumberRange = {
  min?: number;
  max?: number;
  /** Round before clamping. For counts — days, months, headcount. */
  integer?: boolean;
};

export const clampToRange = (
  n: number,
  { min, max, integer }: NumberRange = {},
): number => {
  let v = integer ? Math.round(n) : n;
  if (min !== undefined) v = Math.max(min, v);
  if (max !== undefined) v = Math.min(max, v);
  return v;
};

/**
 * Clamp the contents of a text input, as a string.
 *
 * An empty box is returned empty: blank means "not answered yet", not `min`,
 * and filling it in on the user's behalf answers a question they have not
 * reached. Text that is not a number is returned untouched, so the form's own
 * validation reports it rather than this silently replacing it with a number.
 */
export const clampNumericInput = (
  raw: string,
  range: NumberRange = {},
): string => {
  if (raw.trim() === "") return raw;
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return String(clampToRange(n, range));
};
